import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, makeStyles, Modal, Paper } from '@material-ui/core';
import { Typography } from 'antd';
import CustomButton from '../../../../control/button/Button';
import { useDispatch, useSelector } from 'react-redux';
import {
    change,
    Field,
    getFormValues,
    reduxForm,
    reset,
    submit,
} from 'redux-form';
import RadioToggleButton from '../../../../control/radio/RadioToggleButton';
import {
    appendFullNameUserArray,
    optionIsInternal,
    validateRegisterEventFormExternal,
} from '../../FormUtils';
import TextFields from '../../../../control/textfield/TextFields';
import AutoComplete from '../../../../control/autocomplete/AutoComplete';
import AutoCompleteFreeInput from '../../../../control/autocomplete/AutoCompleteFreeInput';
import * as ApiEvent from '../../../../api/ApiEvent';
import * as FocusActions from '../../../../actions/FocusAction';

const useStyles = makeStyles((theme) => ({
    modalLabel: {
        fontSize: '26px',
        fontWeight: '700',
    },
    modalValue: {
        fontWeight: '400',
        fontSize: '26px',
    },
    label: {
        fontSize: '30px',
        fontWeight: '400',
        paddingBottom: '4px',
    },
    btnCancel: {
        backgroundColor: '#808080 !important',
        width: '140px',
    },
    btnRegister: {
        width: '140px',
    },
    paper: {
        maxHeight: '90%',
        overflowY: 'auto',
        overflowX: 'auto',
        backgroundColor: theme.palette.background.paper,
        width: '50%',
        [theme.breakpoints.down('sm')]: {
            width: '75%',
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
        padding: '24px 48px',
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    mainBody: {
        '& .MuiGrid-item': {
            padding: 0,
        },
    },
    footer: {
        paddingTop: '40px',
    },
    disabledTextField: {
        opacity: '0.5',
    },
}));

const formId = 'ModalRegisterEventForm';

const defaultFormValues = {
    isInternal: true,
    empId: null,
    empIdSelected: null,
};

const emailRegex =
    /^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

const ModalRegisterEvent = ({
    open,
    onClose,
    eventId,
    initialize,
    handleSubmit,
    onSubmit,
}) => {
    const classes = useStyles();
    const values = useSelector((state) => getFormValues(formId)(state));
    const dispatch = useDispatch();
    const [selectOptionInternal, setSelectOptionInternal] = useState([]);
    const [selectOptionExternal, setSelectOptionExternal] = useState([]);
    const [tempFieldInternalValue, setTempFieldInternalValue] = useState(null);
    const [isUserSelectEmpId, setIsUserSelectEmpId] = useState(null);
    const [tempFieldExternalValue, setTempFieldExternalValue] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setSelectOptionInternal([]);
        setSelectOptionExternal([]);
        setTempFieldExternalValue(null);
        setTempFieldInternalValue(null);
        setIsUserSelectEmpId(null);
    }, [open]);

    useEffect(() => {
        initialize({ ...defaultFormValues });
    }, []);

    const handleChangeInternalUserSearchValue = useCallback((value) => {
        setTempFieldInternalValue(value);
    }, []);

    const handleChangeExternalUserSearchValue = useCallback((value) => {
        setTempFieldExternalValue(value);
    }, []);

    const handleChangeDropDownInternal = useCallback(
        (selectedValue) => {
            if (selectedValue) {
                setIsUserSelectEmpId(true);
                dispatch(change(formId, 'empIdSelected', selectedValue.empId));
                dispatch(change(formId, 'email', selectedValue.email));
                dispatch(change(formId, 'firstName', selectedValue.firstName));
                dispatch(change(formId, 'lastName', selectedValue.lastName));
                if (selectedValue.org) {
                    const org = selectedValue.org;
                    dispatch(change(formId, 'orgName', org.orgName));
                    dispatch(change(formId, 'pathName', org.pathName));
                    dispatch(change(formId, 'depName', org.depName));
                    dispatch(change(formId, 'divName', org.divName));
                    dispatch(change(formId, 'secName', org.secName));
                }
                if (selectedValue.job) {
                    dispatch(
                        change(
                            formId,
                            'position',
                            selectedValue.job.jobShortName,
                        ),
                    );
                }
            }
        },
        [dispatch],
    );

    const handleChangeDropDownExternal = useCallback(
        (selectedValue) => {
            if (selectedValue) {
                dispatch(change(formId, 'firstName', selectedValue.firstName));
                dispatch(change(formId, 'lastName', selectedValue.lastName));
                dispatch(
                    change(formId, 'companyName', selectedValue.companyName),
                );
                dispatch(change(formId, 'position', selectedValue.position));
            }
        },
        [dispatch],
    );

    const fetchOptions = useCallback(async () => {
        try {
            setIsLoading(true);
            if (values.isInternal === true) {
                // fetch lookup internal
                const res = await ApiEvent.getLookupInternalUserEvent({
                    empId: tempFieldInternalValue,
                });
                if (
                    res.data &&
                    Array.isArray(res.data) &&
                    res.data.length > 0
                ) {
                    const optionData = appendFullNameUserArray(res.data);
                    setSelectOptionInternal(optionData);
                } else {
                    setSelectOptionInternal([]);
                }
            } else if (values.isInternal === false) {
                // fetch lookup external
                const res = await ApiEvent.getLookupExternalUserEvent({
                    email: tempFieldExternalValue,
                });
                if (
                    res.data &&
                    Array.isArray(res.data) &&
                    res.data.length > 0
                ) {
                    setSelectOptionExternal(res.data);
                } else {
                    setSelectOptionExternal([]);
                }
            }
        } catch (error) {
            values.isInternal
                ? setSelectOptionInternal([])
                : setSelectOptionExternal([]);
        } finally {
            setIsLoading(false);
        }
    }, [tempFieldInternalValue, tempFieldExternalValue, values]);

    useEffect(() => {
        if (
            tempFieldInternalValue &&
            tempFieldInternalValue.length > 6 &&
            tempFieldInternalValue.length <= 8
        ) {
            fetchOptions();
        } else {
            setSelectOptionInternal([]);
        }
    }, [tempFieldInternalValue, fetchOptions]);

    useEffect(() => {
        if (tempFieldExternalValue && emailRegex.test(tempFieldExternalValue)) {
            fetchOptions();
        } else {
            setSelectOptionExternal([]);
        }
    }, [tempFieldExternalValue, fetchOptions]);

    const isDisabledSubmit = useMemo(() => {
        if (values) {
            if (values.isInternal === true) {
                return isUserSelectEmpId;
            } else if (values.isInternal === false) {
                return emailRegex.test(values.email);
            }
        }
        return undefined;
    }, [values, isUserSelectEmpId]);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Modal
                open={open}
                onClose={onClose}
                className={classes.modal}
                disableBackdropClick
            >
                {
                    <Paper className={classes.paper}>
                        <Grid container justify="center">
                            <Grid
                                container
                                className={classes.mainBody}
                                spacing={2}
                            >
                                <Grid item xs={12} md={12}>
                                    <Typography variant="subtitle2">
                                        <label className={classes.label}>
                                            ลงทะเบียนเข้างาน
                                        </label>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        name="isInternal"
                                        label="ประเภทผู้เข้าร่วมงาน"
                                        component={RadioToggleButton}
                                        options={optionIsInternal}
                                        xs={12}
                                        md={6}
                                        onChange={() => {
                                            dispatch(reset(formId));
                                            setSelectOptionInternal([]);
                                            setSelectOptionExternal([]);
                                            setTempFieldInternalValue(null);
                                            setTempFieldExternalValue(null);
                                            setIsUserSelectEmpId(null);
                                        }}
                                        required
                                    />
                                </Grid>

                                {values && values.isInternal ? (
                                    <>
                                        {/* พนักงานและลูกจ้างพนักงาน */}
                                        <Grid item xs={12}>
                                            <Field
                                                name="empId"
                                                label="รหัสพนักงาน"
                                                component={
                                                    AutoCompleteFreeInput
                                                }
                                                options={selectOptionInternal}
                                                optionKey={{
                                                    label: 'empId',
                                                    value: 'empId',
                                                }}
                                                placeholder="ไม่ระบุ"
                                                isShowNoSelect={false}
                                                isEditValue={true}
                                                onChangeInputValue={
                                                    handleChangeInternalUserSearchValue
                                                }
                                                loading={isLoading}
                                                onChangeDropDown={
                                                    handleChangeDropDownInternal
                                                }
                                                autoHighlight={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                รหัสพนักงาน :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.empIdSelected
                                                        ? values.empIdSelected
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                อีเมล :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.email
                                                        ? values.email
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                ชื่อ :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.firstName
                                                        ? values.firstName
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                นามสกุล :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.lastName
                                                        ? values.lastName
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                ตำแหน่ง :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.position
                                                        ? values.position
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                สายงาน :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.pathName
                                                        ? values.pathName
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                ฝ่าย :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.depName
                                                        ? values.depName
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                กอง :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.divName
                                                        ? values.divName
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <div className={classes.modalLabel}>
                                                แผนก :{' '}
                                                <span
                                                    className={
                                                        classes.modalValue
                                                    }
                                                >
                                                    {values.secName
                                                        ? values.secName
                                                        : '-'}
                                                </span>
                                            </div>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        {/* บุคคลภายนอก */}
                                        <Grid item xs={12}>
                                            <Field
                                                name="email"
                                                label="อีเมล"
                                                component={
                                                    AutoCompleteFreeInput
                                                }
                                                options={selectOptionExternal}
                                                optionKey={{
                                                    label: 'email',
                                                    value: 'email',
                                                }}
                                                placeholder="กรุณาระบุ"
                                                isShowNoSelect={false}
                                                isEditValue={true}
                                                onChangeInputValue={
                                                    handleChangeExternalUserSearchValue
                                                }
                                                loading={isLoading}
                                                onChangeDropDown={
                                                    handleChangeDropDownExternal
                                                }
                                                freeSolo={true}
                                                autoHighlight={true}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="firstName"
                                                label="ชื่อ"
                                                component={TextFields}
                                                xs={12}
                                                required
                                                placeholder="กรุณาระบุ"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="lastName"
                                                label="นามสกุล"
                                                component={TextFields}
                                                xs={12}
                                                required
                                                placeholder="กรุณาระบุ"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="companyName"
                                                label="หน่วยงาน"
                                                component={TextFields}
                                                xs={12}
                                                required
                                                placeholder="กรุณาระบุ"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                name="position"
                                                label="ตำแหน่ง"
                                                component={TextFields}
                                                xs={12}
                                                required
                                                placeholder="กรุณาระบุ"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                            <Grid
                                container
                                spacing={2}
                                justify="center"
                                className={classes.footer}
                            >
                                <Grid item>
                                    <CustomButton
                                        label="ปิด"
                                        onClick={onClose}
                                        className={classes.btnCancel}
                                    />
                                </Grid>
                                {
                                    <Grid item>
                                        <CustomButton
                                            label="ลงทะเบียนเข้างาน"
                                            className={classes.btnRegister}
                                            disabled={!isDisabledSubmit}
                                            onClick={() => {
                                                dispatch(submit(formId));
                                            }}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                    </Paper>
                }
            </Modal>
        </form>
    );
};

export default reduxForm({
    form: formId,
    validate: validateRegisterEventFormExternal,
    onSubmitFail: (errors, dispatch, submitError, props) =>
        dispatch(FocusActions.setFocusName(errors, props)),
})(ModalRegisterEvent);
